// apps/client/src/components/FortuneWheelModal.tsx
import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { api, WheelPrize } from '../lib/api';
import { useUserStore } from '../store/userStore';
import { GlassCard } from '../ui-kit/GlassCard';
import Icon from '../ui-kit/Icon';
import { PerfectCoin } from '../ui-kit/OnboardingSVGs';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

// Weights map roughly to angles (total 100)
// Try Again (1): 30
// Try Again (2): 20
// Try Again (3): 20
// 20 Chips: 10
// 50 Chips: 8
// 500 Chips: 10
// 1 Ticket: 2
const HARDCODED_WHEEL_CONFIG = [
    { type: 'NOTHING', amount: 0, name: 'Try Again', weight: 25, color: '#FF3B30' },
    { type: 'CHIPS', amount: 20, name: '20 Chips', weight: 15, color: '#5856D6' },
    { type: 'NOTHING', amount: 0, name: 'Try Again', weight: 20, color: '#FF9500' },
    { type: 'CHIPS', amount: 50, name: '50 Chips', weight: 15, color: '#34C759' },
    { type: 'NOTHING', amount: 0, name: 'Try Again', weight: 15, color: '#FF2D55' },
    { type: 'CHIPS', amount: 500, name: '500 Chips', weight: 8, color: '#007AFF' },
    { type: 'TICKETS', amount: 1, name: '1 Ticket', weight: 2, color: '#AF52DE' },
];

export function FortuneWheelModal({ isOpen, onClose }: Props) {
    const { t } = useTranslation();
    const { refreshBalance, watchAd } = useUserStore();
    const [status, setStatus] = useState<'LOADING' | 'READY' | 'SPINNING' | 'FINISHED' | 'ERROR'>('LOADING');
    const [hasFreeSpin, setHasFreeSpin] = useState(false);
    const [wonPrize, setWonPrize] = useState<WheelPrize | null>(null);
    const [spinRotation, setSpinRotation] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [isWatchingAd, setIsWatchingAd] = useState(false);

    const currentRotationRef = useRef(0);

    useEffect(() => {
        if (isOpen) {
            checkStatus();
        } else {
            setStatus('LOADING');
            setWonPrize(null);
            setSpinRotation(0);
            currentRotationRef.current = 0;
            setErrorMessage('');
            setIsWatchingAd(false);
        }
    }, [isOpen]);

    const checkStatus = async () => {
        try {
            const [statusRes] = await Promise.all([
                api.getWheelStatus(),
            ]);
            setHasFreeSpin(statusRes.hasFreeSpin);
            setStatus('READY');
        } catch (err: any) {
            console.error('Wheel err:', err);
            setErrorMessage(err.message || 'Failed to load wheel');
            setStatus('ERROR');
        }
    };

    const attemptSpin = async (useAd: boolean = false) => {
        if (status !== 'READY') return;

        let adTokenToUse: string | undefined = undefined;

        if (useAd) {
            setIsWatchingAd(true);
            const adResult = await watchAd('fortune_wheel_spin');
            setIsWatchingAd(false);
            if (!adResult.success || !adResult.adToken) {
                setErrorMessage('Ad was not completed or verified. Spin cancelled.');
                return;
            }
            adTokenToUse = adResult.adToken;
            // Temporarily trick the UI into thinking it has a free spin
            setHasFreeSpin(true);
        }

        setStatus('SPINNING');
        setErrorMessage('');

        try {
            const res = await api.spinWheel(adTokenToUse);
            setWonPrize(res.prize);

            // Calculate Landing Angle based on unequal slices
            let targetIndex = -1;

            // Map the API prize to our Visual config using amount/type
            // If the API prize matches multiple slices (e.g., Try Again), pick a random one matching the criteria
            const possibleSlices = HARDCODED_WHEEL_CONFIG.map((c, i) => ({ ...c, index: i })).filter(
                c => c.type === res.prize.type && c.amount === res.prize.amount
            );

            if (possibleSlices.length > 0) {
                // Pick a random matching slice
                const randomChoice = possibleSlices[Math.floor(Math.random() * possibleSlices.length)]!;
                targetIndex = randomChoice.index;
            } else {
                targetIndex = 0; // Fallback
            }

            // Calculate cumulative angles to find the center of the target slice
            let currentAngleAccumulator = 0;
            let targetCenterAngle = 0;

            for (let i = 0; i < HARDCODED_WHEEL_CONFIG.length; i++) {
                const sliceDegrees = (HARDCODED_WHEEL_CONFIG[i]!.weight / 100) * 360;
                if (i === targetIndex) {
                    targetCenterAngle = currentAngleAccumulator + (sliceDegrees / 2);
                    break;
                }
                currentAngleAccumulator += sliceDegrees;
            }

            // Target angle (where the arrow is pointing at 0 degrees / top)
            const targetAngleToTop = -(targetCenterAngle);

            const extraSpins = 8 * 360; // 8 full rotations for drama
            const currentR = currentRotationRef.current;
            const currentMod = ((currentR % 360) + 360) % 360;
            const normalizedTargetAngle = ((targetAngleToTop % 360) + 360) % 360;

            let rotationToAdd = extraSpins + (normalizedTargetAngle - currentMod);
            if (rotationToAdd < extraSpins) {
                rotationToAdd += 360;
            }

            // Add a tiny random offset so it doesn't always land precisely dead center (+- slice bounds)
            const sliceWidth = (HARDCODED_WHEEL_CONFIG[targetIndex]!.weight / 100) * 360;
            const jitter = (Math.random() - 0.5) * (sliceWidth * 0.8);
            rotationToAdd += jitter;

            const newRotation = currentR + rotationToAdd;
            currentRotationRef.current = newRotation;
            setSpinRotation(newRotation);

            setTimeout(() => {
                setStatus('FINISHED');
                refreshBalance();
            }, 6500); // 6.5s animation

        } catch (err: any) {
            setErrorMessage(err.message || 'Spin failed');
            setStatus('ERROR');
        }
    };

    if (!isOpen) return null;

    // Build the SVG conic gradient logic
    let startAngle = 0;
    const slicesSVG = HARDCODED_WHEEL_CONFIG.map((slice, i) => {
        const sliceDegrees = (slice.weight / 100) * 360;
        const midAngle = startAngle + (sliceDegrees / 2);
        const start = startAngle;
        startAngle += sliceDegrees;

        // Convert angles to Cartesian paths for SVG pie slices
        // Not using simple conic-gradient because we want exact control over borders and text placement per unequal slice
        const x1 = 50 + 50 * Math.cos((Math.PI * (start - 90)) / 180);
        const y1 = 50 + 50 * Math.sin((Math.PI * (start - 90)) / 180);
        const x2 = 50 + 50 * Math.cos((Math.PI * (start + sliceDegrees - 90)) / 180);
        const y2 = 50 + 50 * Math.sin((Math.PI * (start + sliceDegrees - 90)) / 180);
        const largeArc = sliceDegrees > 180 ? 1 : 0;
        const pathData = `M50,50 L${x1},${y1} A50,50 0 ${largeArc},1 ${x2},${y2} Z`;

        return (
            <g key={`slice-${i}`}>
                <path d={pathData} fill={slice.color} stroke="#1A1A2E" strokeWidth="0.5" />
                <g transform={`rotate(${midAngle} 50 50)`}>
                    <g transform={`translate(50, 10)`}>
                        {slice.type === 'CHIPS' && (
                            <g transform="translate(-6, 0) scale(0.12)">
                                <PerfectCoin />
                            </g>
                        )}
                        {slice.type === 'NOTHING' && (
                            <text x="0" y="8" fontSize="6" fill="#FFF" textAnchor="middle" fontWeight="bold">💔</text>
                        )}
                        {slice.type === 'TICKETS' && (
                            <text x="0" y="8" fontSize="6" fill="#FFF" textAnchor="middle" fontWeight="bold">🎟️</text>
                        )}
                        <text x="0" y="15" fontSize="3" fill="#FFF" textAnchor="middle" fontWeight="bold" className="shadow-black drop-shadow-md">
                            {slice.amount > 0 ? slice.amount : slice.name}
                        </text>
                    </g>
                </g>
            </g>
        );
    });

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050210]/90 backdrop-blur-md"
            >
                <div className="absolute inset-0 bg-[url('/assets/images/confetti.png')] bg-repeat opacity-10 pointer-events-none mix-blend-screen" />

                <motion.div
                    initial={{ scale: 0.9, y: 30 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, y: 30 }}
                    transition={{ type: "spring", bounce: 0.4 }}
                    className="w-full max-w-md relative z-10"
                >
                    <GlassCard variant="glow" className="p-8 relative overflow-hidden text-center flex flex-col items-center border border-white/10 shadow-2xl rounded-3xl bg-gradient-to-br from-[#1A1A2E]/80 to-[#0F0F1A]/80">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 transition-all z-20"
                        >
                            <Icon name="close" />
                        </button>

                        <h2 className="text-3xl font-black font-display text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 mb-1 tracking-tight">{t('wheel.title')}</h2>
                        <p className="text-white/60 text-sm mb-8 font-medium">{t('wheel.subtitle')}</p>

                        {status === 'LOADING' && (
                            <div className="py-12"><span className="spinner text-[#FFC107] text-4xl">⚙️</span></div>
                        )}

                        {status === 'ERROR' && (
                            <div className="py-8">
                                <span className="text-5xl block mb-3 text-red-500 drop-shadow-md">❌</span>
                                <p className="text-red-400 font-bold mb-4">{errorMessage}</p>
                                <button className="btn btn-primary w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white" onClick={checkStatus}>{t('wheel.retry')}</button>
                            </div>
                        )}

                        {(status === 'READY' || status === 'SPINNING' || status === 'FINISHED') && (
                            <div className="relative w-[320px] h-[320px] mx-auto mb-10 pointer-events-none">
                                {/* The Pointer/Arrow at the top */}
                                <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-20 w-12 h-12 flex flex-col items-center justify-center drop-shadow-[0_5px_10px_rgba(0,0,0,0.8)]">
                                    <svg viewBox="0 0 24 24" className="w-12 h-12 text-[#FFC107] fill-current">
                                        <path d="M12 22L23.2583 4H0.74167L12 22Z" />
                                    </svg>
                                </div>

                                {/* The Wheel Base */}
                                <div className="absolute inset-[-10px] rounded-full bg-gradient-to-br from-[#FFD700] to-[#B8860B] shadow-[0_0_40px_rgba(255,215,0,0.3)] flex items-center justify-center">
                                    <div className="absolute inset-[4px] rounded-full bg-[#1A1A2E]" />

                                    {/* The Spinning SVG Wheel */}
                                    <motion.div
                                        className="w-[300px] h-[300px] rounded-full relative overflow-hidden"
                                        animate={{ rotate: spinRotation }}
                                        transition={{ duration: 6.5, ease: [0.15, 0.45, 0.05, 1], type: 'tween' }}
                                    >
                                        <svg viewBox="0 0 100 100" className="w-full h-full">
                                            {slicesSVG}
                                        </svg>
                                    </motion.div>

                                    {/* Inner center circle */}
                                    <div className="absolute inset-0 m-auto w-16 h-16 rounded-full bg-gradient-to-br from-[#2D2B55] to-[#1A1A2E] border-4 border-[#FFD700] shadow-[0_5px_15px_rgba(0,0,0,0.8)] flex items-center justify-center z-10 text-2xl drop-shadow-md">
                                        ⭐️
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Controls Bottom Area */}
                        {(status === 'READY') && (
                            <div className="w-full relative z-10">
                                {hasFreeSpin ? (
                                    <button
                                        className="w-full py-4 rounded-2xl font-black text-xl bg-gradient-to-r from-[#FFC107] to-[#F59E0B] text-[#1a0b2e] shadow-[0_0_20px_rgba(255,193,7,0.4)] hover:shadow-[0_0_30px_rgba(255,193,7,0.6)] animate-pulse transition-all transform hover:scale-[1.02] active:scale-95"
                                        onClick={() => attemptSpin(false)}
                                    >
                                        {t('wheel.freeSpin')}
                                    </button>
                                ) : (
                                    <div className="w-full flex justify-center items-center">
                                        <button
                                            disabled={isWatchingAd}
                                            onClick={() => attemptSpin(true)}
                                            className={`
                                                w-full py-4 rounded-2xl font-black text-lg text-white transition-all transform hover:scale-[1.02] active:scale-95
                                                bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-[0_0_20px_rgba(124,58,237,0.4)]
                                                ${isWatchingAd ? 'opacity-50 cursor-not-allowed' : ''}
                                            `}
                                        >
                                            {isWatchingAd ? t('wheel.loadingAd') : t('wheel.watchAdSpin')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {status === 'FINISHED' && wonPrize && (
                            <motion.div
                                initial={{ scale: 0, opacity: 0, y: 20 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                className="w-full relative z-10"
                            >
                                <div className="bg-gradient-to-br from-[#FFC107]/20 to-[#F59E0B]/10 border border-[#FFC107]/50 rounded-2xl p-6 shadow-[0_0_40px_rgba(255,193,7,0.2)]">
                                    <h3 className="text-2xl font-black text-white drop-shadow-md mb-3">{wonPrize.type === 'NOTHING' ? t('wheel.betterLuck') : t('wheel.youWon')}</h3>

                                    <div className="flex flex-col items-center justify-center gap-2 mb-2">
                                        {wonPrize.type === 'CHIPS' && <div className="w-16 h-16"><PerfectCoin size={64} /></div>}
                                        {wonPrize.type === 'NOTHING' && <span className="text-5xl drop-shadow-xl">💔</span>}
                                        {wonPrize.type === 'TICKETS' && <span className="text-5xl drop-shadow-xl">🎟️</span>}

                                        <span className="text-4xl font-display font-black text-[#FFC107] drop-shadow-md">
                                            {wonPrize.amount > 0 ? wonPrize.amount : ''}
                                        </span>
                                    </div>
                                    <p className="text-white/80 font-bold">{wonPrize.name}</p>
                                </div>
                                <button className="w-full py-4 mt-6 rounded-2xl font-black text-lg bg-white/10 hover:bg-white/20 text-white transition-colors border border-white/20" onClick={onClose}>
                                    {t('wheel.awesome')}
                                </button>
                            </motion.div>
                        )}
                    </GlassCard>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
